/**
 * Your listings & work — the builder's side of Get it built: estimates, orders
 * being built, client apps paying monthly, earnings and payouts, the public
 * profile + payout bank account, and invitations (GET /build/me).
 * Ready-made Marketplace listings stay on /marketplace/seller (linked here).
 * Doc: docs/marketplace_get_it_built.md
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { fmtDate, money, mktError } from '@/api/marketplace'
import {
  getBuilderDashboard, ORDER_STATUS_LABELS, STAGE_STATUS_LABELS, updateBuilderProfile,
  type BuilderDashboard, type BuilderProfile, type OrderStatus, type StageStatus,
} from '@/api/marketplaceBuild'
import MarketplaceShell from '../MarketplaceShell'
import { Avatar, budgetText, EligibleBanner, KindPill, Loading, QuotePill, relTime } from './builderUi'

type Tab = 'estimates' | 'orders' | 'apps' | 'earnings' | 'profile' | 'invitations'
const TABS: Tab[] = ['estimates', 'orders', 'apps', 'earnings', 'profile', 'invitations']

const ORDER_TONE: Record<OrderStatus, string> = { placed: 'warn', in_progress: 'info', completed: 'ok', declined: 'bad', cancelled: '' }
const STAGE_TONE: Record<StageStatus, string> = {
  pending: '', payment_due: 'warn', in_review: 'info', changes_requested: 'warn', approved: 'ok', released: 'ok', disputed: 'bad', refunded: '',
}
const SUB_STATUS: Record<string, [string, string]> = {
  trial: ['Free trial', 'info'], active: ['Paying', 'ok'], past_due: ['Payment overdue', 'bad'], ended: ['Ended', ''], deleted: ['Removed', ''], installing: ['Setting up', 'info'],
}

/** Sum a field per currency → "₹1,000 + $20". */
function perCurrency(rows: { currency: string; v: number }[], fallbackCur: string): string {
  const by: Record<string, number> = {}
  for (const r of rows) by[r.currency] = (by[r.currency] || 0) + r.v
  const keys = Object.keys(by)
  if (!keys.length) return money(0, fallbackCur)
  return keys.map((c) => money(by[c], c)).join(' + ')
}

export default function BuilderDashboardPage() {
  const [params, setParams] = useSearchParams()
  const tabParam = params.get('tab') as Tab | null
  const tab: Tab = tabParam && TABS.includes(tabParam) ? tabParam : 'estimates'
  const setTab = (t: Tab) => { const n = new URLSearchParams(params); n.set('tab', t); setParams(n, { replace: true }) }

  const [d, setD] = useState<BuilderDashboard | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(async () => {
    try {
      setD(await getBuilderDashboard())
      setError(null)
    } catch (e) {
      setError(mktError(e, "We couldn't load your work.").message)
    }
  }, [])
  useEffect(() => { load() }, [load])

  if (error && !d) return <MarketplaceShell><div className="mkt-wrap" style={{ padding: '32px 20px' }}><div role="alert" className="mkt-banner bad"><div><strong>Something went wrong</strong>{error}</div><button type="button" className="btn btn-secondary" onClick={load}>Try again</button></div></div></MarketplaceShell>
  if (!d) return <MarketplaceShell><Loading text="Loading your work…" /></MarketplaceShell>

  const cur = d.earnings[0]?.currency || d.orders[0]?.currency || 'INR'
  const balance = perCurrency(d.earnings.map((e) => ({ currency: e.currency, v: e.balance_minor })), cur)
  const held = perCurrency(d.orders.map((o) => ({ currency: o.currency, v: o.money.held_minor })), cur)
  const todo: { key: string; text: string; to?: string; tab?: Tab; cta: string }[] = []
  for (const e of d.estimates) if (e.status === 'revision_asked' && e.request) todo.push({ key: `q${e.id}`, text: `The client asked for changes to your estimate for “${e.request.title}”.`, to: `/marketplace/work/${e.request.id}`, cta: 'Revise estimate' })
  for (const o of d.orders) {
    if (o.status === 'placed') todo.push({ key: `o${o.id}`, text: `New order ${o.order_no}: “${o.title}” is waiting for you to accept it.`, to: `/marketplace/build/orders/${o.id}`, cta: 'Open order' })
    else if (o.current?.status === 'changes_requested') todo.push({ key: `c${o.id}`, text: `${o.client} asked for changes to “${o.current.name}” on ${o.title}.`, to: `/marketplace/build/orders/${o.id}`, cta: 'Open order' })
  }
  if (d.profile && !d.profile.bank?.last4 && (d.orders.length || d.earnings.some((e) => e.balance_minor > 0))) todo.push({ key: 'bank', text: 'Add the bank account Twasta pays your earnings into.', tab: 'profile', cta: 'Add bank account' })

  const labels: Record<Tab, string> = {
    estimates: `Estimates (${d.estimates.length})`, orders: `Being built (${d.orders.filter((o) => o.status === 'placed' || o.status === 'in_progress').length})`,
    apps: `Client apps (${d.client_apps.length})`, earnings: 'Earnings & payouts', profile: 'Profile & bank', invitations: `Invitations (${d.invitations.length})`,
  }

  return (
    <MarketplaceShell>
      <div className="mkt-wrap mkt-col" style={{ padding: '24px 20px 56px', gap: 18 }}>
        <div className="mkt-row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div className="mkt-col" style={{ gap: 2 }}>
            <h1 style={{ fontSize: 28 }}>Your listings &amp; work</h1>
            <span className="mkt-muted">{todo.length ? `${todo.length} thing${todo.length === 1 ? '' : 's'} need${todo.length === 1 ? 's' : ''} you.` : 'Estimates, orders and what you earn from building for clients.'}</span>
          </div>
          <div className="mkt-row">
            <Link to="/marketplace/seller" className="btn btn-ghost">Your Marketplace listings</Link>
            <Link to="/marketplace/work" className="btn btn-primary">Find more work</Link>
          </div>
        </div>

        <EligibleBanner eligible={d.eligible} />
        {error && <div role="alert" className="mkt-banner bad"><div>{error}</div></div>}

        {todo.length > 0 && (
          <div className="mkt-col" style={{ gap: 8 }}>
            {todo.map((t) => (
              <div key={t.key} className="mkt-banner info" style={{ alignItems: 'center', padding: '10px 14px' }}>
                <div style={{ fontSize: 14 }}>{t.text}</div>
                {t.to ? <Link to={t.to} className="btn btn-primary btn-sm">{t.cta}</Link> : <button type="button" className="btn btn-primary btn-sm" onClick={() => t.tab && setTab(t.tab)}>{t.cta}</button>}
              </div>
            ))}
          </div>
        )}

        <div className="mkt-kpis">
          <div className="mkt-dark mkt-col" style={{ gap: 4 }}><span className="mkt-muted">Your earnings balance</span><strong style={{ fontSize: 28 }}>{balance}</strong><span className="mkt-muted" style={{ fontSize: 13 }}>Released to you, waiting for Twasta to pay your bank</span></div>
          <div className="mkt-kpi"><span className="mkt-muted">Held by Twasta for you</span><strong>{held}</strong><span className="mkt-faint">Paid in by clients, released as they approve each stage</span></div>
          <div className="mkt-kpi"><span className="mkt-muted">Rating</span><strong>{d.stats.rating != null ? `${d.stats.rating} ★` : '—'}</strong><span className="mkt-faint">{d.stats.reviews ? `${d.stats.reviews} review${d.stats.reviews === 1 ? '' : 's'} · ` : ''}{d.stats.completed ?? 0} order{d.stats.completed === 1 ? '' : 's'} finished</span></div>
          <div className="mkt-kpi"><span className="mkt-muted">Client apps in use</span><strong>{d.stats.apps_live ?? 0}</strong><span className="mkt-faint">{d.stats.on_time_pct != null ? `${d.stats.on_time_pct}% finished on time` : 'Paying you every month'}</span></div>
        </div>

        <div role="tablist" aria-label="Dashboard sections" className="mkt-tabs">
          {TABS.map((t) => <button key={t} type="button" role="tab" aria-selected={tab === t} className={`mkt-tab${tab === t ? ' on' : ''}`} onClick={() => setTab(t)}>{labels[t]}</button>)}
        </div>

        <div role="tabpanel" className="mkt-col" style={{ gap: 14 }}>
          {tab === 'estimates' && <EstimatesTab d={d} />}
          {tab === 'orders' && <OrdersTab d={d} />}
          {tab === 'apps' && <ClientAppsTab d={d} />}
          {tab === 'earnings' && <EarningsTab d={d} onBank={() => setTab('profile')} />}
          {tab === 'profile' && <ProfileTab profile={d.profile} onSaved={load} />}
          {tab === 'invitations' && <InvitationsTab d={d} />}
        </div>
      </div>
    </MarketplaceShell>
  )
}

function EstimatesTab({ d }: { d: BuilderDashboard }) {
  if (!d.estimates.length) return <div className="mkt-empty"><strong style={{ fontSize: 18 }}>No estimates yet</strong><span className="mkt-muted">Find a request you'd like to build and send your price.</span><Link to="/marketplace/work" className="btn btn-primary">Find work</Link></div>
  return (
    <div className="mkt-scroll">
      <table className="mkt-table" style={{ minWidth: 760 }}>
        <thead><tr><th scope="col">Request</th><th scope="col">Sent</th><th scope="col" className="num">Your estimate</th><th scope="col">Status</th><th scope="col"><span className="mkt-sr">Open</span></th></tr></thead>
        <tbody>
          {d.estimates.map((e) => (
            <tr key={e.id}>
              <td>
                {e.request ? <Link to={`/marketplace/work/${e.request.id}`}><strong>{e.request.title}</strong></Link> : <strong>Request no longer available</strong>}
                <div className="mkt-row" style={{ gap: 6, marginTop: 4 }}>{e.request && <KindPill kind={e.request.kind} />}{e.request && e.request.status !== 'open' && <span className="mkt-faint">Request {e.request.status === 'ordered' ? 'ordered' : 'closed'}</span>}</div>
              </td>
              <td>{e.sent_at ? fmtDate(e.sent_at) : '—'}<div className="mkt-faint">Version {e.version}</div></td>
              <td className="num"><strong>{money(e.price_minor, e.currency)}</strong><div className="mkt-faint">{e.weeks ? `${e.weeks} weeks` : ''}</div></td>
              <td><QuotePill status={e.status} />{e.shortlisted && <> <span className="mkt-pill ok">Shortlisted</span></>}{e.expired && <> <span className="mkt-pill bad">Expired</span></>}</td>
              <td>{e.request && <Link to={`/marketplace/work/${e.request.id}`} className="btn btn-secondary btn-sm">{e.status === 'draft' ? 'Finish & send' : e.status === 'revision_asked' ? 'Revise' : 'Open'}</Link>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function OrdersTab({ d }: { d: BuilderDashboard }) {
  if (!d.orders.length) return <div className="mkt-empty"><strong style={{ fontSize: 18 }}>Nothing being built yet</strong><span className="mkt-muted">When a client orders from your estimate, it shows up here.</span></div>
  return (
    <div className="mkt-col" style={{ gap: 12 }}>
      {d.orders.map((o) => (
        <article key={o.id} className="mkt-panel mkt-col" style={{ gap: 10, padding: 18 }}>
          <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
            <div className="mkt-col" style={{ gap: 2 }}>
              <div className="mkt-row" style={{ gap: 8 }}><strong style={{ fontSize: 17 }}>{o.title}</strong><span className={`mkt-pill ${ORDER_TONE[o.status]}`}>{ORDER_STATUS_LABELS[o.status] || o.status}</span></div>
              <span className="mkt-muted" style={{ fontSize: 14 }}>{o.order_no} · for {o.client} · {money(o.price_minor, o.currency)} · ordered {fmtDate(o.created_at)}</span>
            </div>
            <Link to={`/marketplace/build/orders/${o.id}`} className="btn btn-primary btn-sm">Open order</Link>
          </div>
          {o.current && (
            <div className="mkt-row" style={{ gap: 8, fontSize: 14 }}>
              <span className="mkt-muted">Current stage:</span><strong>{o.current.name}</strong>
              <span className={`mkt-pill ${STAGE_TONE[o.current.status]}`}>{STAGE_STATUS_LABELS[o.current.status] || o.current.status}</span>
            </div>
          )}
          <div className="mkt-row" style={{ gap: '6px 20px', fontSize: 14 }}>
            <span><span className="mkt-faint">Paid to you</span> <strong>{money(o.money.paid_to_builder_minor, o.currency)}</strong></span>
            <span><span className="mkt-faint">Held by Twasta</span> <strong>{money(o.money.held_minor, o.currency)}</strong></span>
            <span><span className="mkt-faint">Still to come from the client</span> <strong>{money(o.money.still_to_pay_minor, o.currency)}</strong></span>
          </div>
          {o.price_minor > 0 && (
            <div className="mkt-meter" role="img" aria-label={`Released ${money(o.money.released_gross_minor, o.currency)} of ${money(o.price_minor, o.currency)}`}>
              <div style={{ width: `${Math.min(100, (o.money.released_gross_minor / o.price_minor) * 100)}%`, background: 'var(--color-success)' }} />
            </div>
          )}
        </article>
      ))}
    </div>
  )
}

function ClientAppsTab({ d }: { d: BuilderDashboard }) {
  return (
    <div className="mkt-col" style={{ gap: 12 }}>
      <p className="mkt-muted" style={{ margin: 0 }}>Apps you built for one client, installed in their organization. They pay monthly; you keep the app updated from your own account.</p>
      {d.client_apps.length === 0 ? <div className="mkt-empty"><strong style={{ fontSize: 18 }}>No client apps yet</strong><span className="mkt-muted">An app joins this list when its client signs off the last stage and it is installed for them.</span></div> : (
        <div className="mkt-scroll">
          <table className="mkt-table" style={{ minWidth: 820 }}>
            <thead><tr><th scope="col">App</th><th scope="col">Client</th><th scope="col">Plan</th><th scope="col">Status</th><th scope="col" className="num">Client pays</th><th scope="col" className="num">Hosting</th><th scope="col" className="num">Fee</th><th scope="col" className="num">You earn</th></tr></thead>
            <tbody>
              {d.client_apps.map((a) => {
                const [st, tone] = SUB_STATUS[a.status] || [a.status, '']
                const lc = a.last_charge
                return (
                  <tr key={a.order_id}>
                    <td><Link to={`/marketplace/build/orders/${a.order_id}`}><strong>{a.title}</strong></Link></td>
                    <td>{a.client}</td>
                    <td>{a.basis === 'server' ? `${a.size || ''} server` : `${a.seats ?? '—'} people`}<div className="mkt-faint">{money(a.price_minor, a.currency)}{a.basis === 'server' ? ' a month' : ' per person a month'}</div></td>
                    <td><span className={`mkt-pill ${tone}`}>{st}</span></td>
                    <td className="num">{lc ? money(lc.amount_minor, a.currency) : '—'}</td>
                    <td className="num">{lc ? `− ${money(lc.hosting_minor, a.currency)}` : '—'}</td>
                    <td className="num">{lc ? `− ${money(lc.fee_minor, a.currency)}` : '—'}</td>
                    <td className="num" style={{ fontWeight: 700, color: 'var(--color-success)' }}>{lc ? money(lc.earn_minor, a.currency) : '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <span className="mkt-faint">Figures are each app's latest monthly charge.</span>
        </div>
      )}
    </div>
  )
}

function EarningsTab({ d, onBank }: { d: BuilderDashboard; onBank: () => void }) {
  const bank = d.profile?.bank
  return (
    <div className="mkt-split">
      <div className="mkt-main" style={{ gap: 16 }}>
        <section className="mkt-panel mkt-col" style={{ gap: 10 }}>
          <h2>Released to you, stage by stage</h2>
          {d.ledger.length === 0 ? <span className="mkt-muted">Nothing released yet. Twasta pays you for a stage once the client approves it.</span> : (
            <div className="mkt-scroll">
              <table className="mkt-table" style={{ minWidth: 760 }}>
                <thead><tr><th scope="col">Date</th><th scope="col">What</th><th scope="col" className="num">Released</th><th scope="col" className="num">Twasta fee</th><th scope="col" className="num">Tax on fee</th><th scope="col" className="num">TDS</th><th scope="col" className="num">You got</th></tr></thead>
                <tbody>
                  {d.ledger.map((r, i) => (
                    <tr key={`${r.order_id}-${r.stage}-${i}`}>
                      <td>{r.paid_at ? fmtDate(r.paid_at) : '—'}</td>
                      <td><Link to={`/marketplace/build/orders/${r.order_id}`}>{r.title}</Link><div className="mkt-faint">{r.order_no} · {r.stage}</div></td>
                      <td className="num">{money(r.gross_minor, r.currency)}</td>
                      <td className="num">− {money(r.fee_minor, r.currency)}</td>
                      <td className="num">{r.fee_tax_minor ? `− ${money(r.fee_tax_minor, r.currency)}` : '—'}</td>
                      <td className="num">{r.tds_minor ? `− ${money(r.tds_minor, r.currency)}` : '—'}</td>
                      <td className="num" style={{ fontWeight: 700, color: 'var(--color-success)' }}>{money(r.net_minor, r.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <section className="mkt-panel mkt-col" style={{ gap: 10 }}>
          <h2>Paid to your bank</h2>
          {d.payouts.length === 0 ? <span className="mkt-muted">No payouts yet. Twasta finance pays your earnings balance to your bank and records the bank reference (UTR) here.</span> : (
            <div className="mkt-scroll">
              <table className="mkt-table" style={{ minWidth: 560 }}>
                <thead><tr><th scope="col">Date</th><th scope="col" className="num">Amount</th><th scope="col">Bank reference (UTR)</th><th scope="col">How</th><th scope="col">Note</th></tr></thead>
                <tbody>{d.payouts.map((p) => <tr key={p.id}><td>{fmtDate(p.paid_at)}</td><td className="num"><strong>{money(p.amount_minor, p.currency)}</strong></td><td style={{ fontFamily: 'var(--font-mono, monospace)' }}>{p.utr}</td><td>{p.method || '—'}</td><td>{p.note || '—'}</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </section>
      </div>
      <aside className="mkt-aside">
        <div className="mkt-dark mkt-col" style={{ gap: 6 }}>
          <span className="mkt-muted">Earnings balance</span>
          {d.earnings.length === 0 ? <strong style={{ fontSize: 28 }}>{money(0, 'INR')}</strong> : d.earnings.map((e) => <strong key={e.currency} style={{ fontSize: 28 }}>{money(e.balance_minor, e.currency)}</strong>)}
          <span className="mkt-muted" style={{ fontSize: 13 }}>Twasta finance sends this to your bank; you'll get an email with the bank reference.</span>
        </div>
        <div className="mkt-panel mkt-col" style={{ gap: 8, padding: 18, fontSize: 14 }}>
          <strong>Payout account</strong>
          {bank?.last4 ? (
            <>
              <span>{bank.name || 'Bank'} ••••{bank.last4} <span className={`mkt-pill ${bank.verified ? 'ok' : 'warn'}`}>{bank.verified ? 'Verified' : 'Waiting for Twasta to verify'}</span></span>
              <span className="mkt-faint">PAN {bank.pan ? 'on file' : 'not added'} · GSTIN {bank.gstin ? 'on file' : 'not added'}</span>
            </>
          ) : <span className="mkt-muted">No bank account yet.</span>}
          <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={onBank}>{bank?.last4 ? 'Change' : 'Add bank account'}</button>
        </div>
      </aside>
    </div>
  )
}

function ProfileTab({ profile, onSaved }: { profile: BuilderProfile | null; onSaved: () => void }) {
  const p = profile
  const [f, setF] = useState(() => ({
    display_name: p?.name || '', headline: p?.headline || '', about: p?.about || '', skills: (p?.skills || []).join(', '),
    location: p?.location || '', rate_note: p?.rate_note || '', available: p?.available ?? true,
  }))
  const [bank, setBank] = useState(() => ({ holder: p?.bank?.holder || '', name: p?.bank?.name || '', account: '', ifsc: p?.bank?.ifsc || '', pan: p?.bank?.pan || '', gstin: p?.bank?.gstin || '' }))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      const { account, ...rest } = bank
      await updateBuilderProfile({
        ...f, skills: f.skills.split(',').map((s) => s.trim()).filter(Boolean),
        bank: account.trim() ? { ...rest, account: account.trim() } : rest,
      })
      toast.success('Profile saved')
      setBank((b) => ({ ...b, account: '' }))
      onSaved()
    } catch (ex) {
      const m = mktError(ex, "We couldn't save your profile.").message
      setErr(m)
      toast.error(m)
    } finally {
      setBusy(false)
    }
  }

  const input = (id: string, label: string, value: string, on: (v: string) => void, extra?: { placeholder?: string; hint?: string; type?: string; autoComplete?: string }) => (
    <div className="mkt-field">
      <label htmlFor={id}>{label}</label>
      <input id={id} type={extra?.type || 'text'} value={value} placeholder={extra?.placeholder} autoComplete={extra?.autoComplete} onChange={(e) => on(e.target.value)} />
      {extra?.hint && <span className="mkt-faint">{extra.hint}</span>}
    </div>
  )

  return (
    <form onSubmit={save} className="mkt-split">
      <section className="mkt-main mkt-panel" style={{ gap: 14 }} aria-label="Public profile">
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <h2>Public profile</h2>
          {p && <Link to={`/marketplace/builders/${encodeURIComponent(p.tenant_id)}`} className="btn btn-ghost btn-sm">See it as clients do</Link>}
        </div>
        <div className="mkt-row" style={{ flexWrap: 'nowrap' }}>
          <Avatar text={p?.initials || ''} size={56} available={f.available} />
          <button type="button" role="switch" aria-checked={f.available} onClick={() => setF({ ...f, available: !f.available })} className="mkt-choice mkt-choice-row" style={{ justifyContent: 'flex-start', maxWidth: 460 }}>
            <span className={`mkt-switch${f.available ? ' on' : ''}`} aria-hidden="true" />
            <span className="mkt-col" style={{ gap: 0 }}><strong style={{ fontSize: 15 }}>Available for new work</strong><span className="mkt-muted" style={{ fontSize: 14 }}>Shown on your profile and to businesses looking for a builder.</span></span>
          </button>
        </div>
        {input('bp-name', 'Name shown to clients', f.display_name, (v) => setF({ ...f, display_name: v }))}
        {input('bp-head', 'Headline', f.headline, (v) => setF({ ...f, headline: v }), { placeholder: 'What you build, in one line' })}
        <div className="mkt-field"><label htmlFor="bp-about">About</label><textarea id="bp-about" rows={5} value={f.about} onChange={(e) => setF({ ...f, about: e.target.value })} placeholder="Who you build for and how you work" /></div>
        {input('bp-skills', 'Skills', f.skills, (v) => setF({ ...f, skills: v }), { placeholder: 'Approvals, dashboards, phone screens', hint: 'Separate with commas.' })}
        <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
          {input('bp-loc', 'Location', f.location, (v) => setF({ ...f, location: v }))}
          {input('bp-rate', 'How you price', f.rate_note, (v) => setF({ ...f, rate_note: v }), { placeholder: 'e.g. Fixed price per app' })}
        </div>
      </section>

      <section className="mkt-aside mkt-panel" style={{ gap: 12 }} aria-label="Payout bank account">
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <h2>Payout bank account</h2>
          {p?.bank?.last4 && <span className={`mkt-pill ${p.bank.verified ? 'ok' : 'warn'}`}>{p.bank.verified ? 'Verified' : 'Not verified yet'}</span>}
        </div>
        <span className="mkt-faint">Twasta pays your earnings into this account. Only you and Twasta finance see it — never clients.</span>
        {input('bk-holder', 'Account holder', bank.holder, (v) => setBank({ ...bank, holder: v }), { autoComplete: 'off' })}
        {input('bk-name', 'Bank name', bank.name, (v) => setBank({ ...bank, name: v }), { autoComplete: 'off' })}
        {input('bk-acct', 'Account number', bank.account, (v) => setBank({ ...bank, account: v.replace(/\s/g, '') }), {
          autoComplete: 'off', placeholder: p?.bank?.last4 ? `••••${p.bank.last4} — type a new number to change it` : 'Full account number',
          hint: p?.bank?.last4 ? 'Leave empty to keep the current account.' : undefined,
        })}
        {input('bk-ifsc', 'IFSC', bank.ifsc, (v) => setBank({ ...bank, ifsc: v.toUpperCase() }), { autoComplete: 'off' })}
        <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          {input('bk-pan', 'PAN', bank.pan, (v) => setBank({ ...bank, pan: v.toUpperCase() }), { autoComplete: 'off' })}
          {input('bk-gstin', 'GSTIN (if registered)', bank.gstin, (v) => setBank({ ...bank, gstin: v.toUpperCase() }), { autoComplete: 'off' })}
        </div>
        <div className="mkt-msg warn" style={{ fontWeight: 500 }}>A changed account holder, IFSC or account number must be verified again by Twasta before your next payout.</div>
        {p?.bank?.changed_at && <span className="mkt-faint">Last changed {fmtDate(p.bank.changed_at)} ({relTime(p.bank.changed_at)}).</span>}
      </section>

      <div className="mkt-row" style={{ flex: '1 1 100%' }}>
        {err && <div role="alert" className="mkt-msg error">{err}</div>}
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save profile'}</button>
      </div>
    </form>
  )
}

function InvitationsTab({ d }: { d: BuilderDashboard }) {
  if (!d.invitations.length) return <div className="mkt-empty"><strong style={{ fontSize: 18 }}>No invitations right now</strong><span className="mkt-muted">When a business invites you to estimate, it shows up here.</span></div>
  return (
    <div className="mkt-col" style={{ gap: 12 }}>
      {d.invitations.map((r) => (
        <article key={r.id} className="mkt-panel mkt-col" style={{ gap: 8, padding: 18 }}>
          <div className="mkt-row" style={{ gap: 6 }}>{r.posted_at && <span className="mkt-faint">Posted {relTime(r.posted_at)}</span>}<KindPill kind={r.kind} label={r.kind_label} />{r.my_quote && <QuotePill status={r.my_quote.status} />}</div>
          <strong style={{ fontSize: 17 }}>{r.title}</strong>
          {r.description && <p className="mkt-muted" style={{ margin: 0, fontSize: 14, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r.description}</p>}
          <span className="mkt-muted" style={{ fontSize: 14 }}>{budgetText(r)}{r.timeline ? ` · ${r.timeline}` : ''}{r.closes_at ? ` · closes ${relTime(r.closes_at)}` : ''}</span>
          <div className="mkt-row"><Link to={`/marketplace/work/${r.id}`} className="btn btn-primary btn-sm">{r.my_quote ? 'Open your estimate' : 'View & estimate'}</Link></div>
        </article>
      ))}
    </div>
  )
}
